import type { GridSelection, Rectangle } from "@glideapps/glide-data-grid";
import { isFormulaCell } from "@/payroll/ai-script/cell-kind-utils";
import {
  AI_SPREADSHEET_CELL_KIND,
  type IAiSpreadsheetCell,
  type IAiSpreadsheetPasteEdit,
} from "@/payroll/ai-script/types";
import { isCellEditable } from "./adapter";
import { translateFormulaReferences } from "./formula-reference-utils";

const positiveModulo = (value: number, divisor: number): number =>
  ((value % divisor) + divisor) % divisor;

const containsCoordinate = (
  rectangle: Rectangle,
  col: number,
  row: number,
): boolean =>
  col >= rectangle.x &&
  col < rectangle.x + rectangle.width &&
  row >= rectangle.y &&
  row < rectangle.y + rectangle.height;

export type CellLookup = (
  colIndex: number,
  rowIndex: number,
) => IAiSpreadsheetCell | undefined;

export const isFormulaSourceRange = (
  source: Rectangle | undefined,
  getCellRecord: CellLookup,
): boolean => {
  if (!source || source.width < 1 || source.height < 1) return false;

  for (let row = source.y; row < source.y + source.height; row += 1) {
    for (let col = source.x; col < source.x + source.width; col += 1) {
      const cell = getCellRecord(col, row);
      if (!cell || !isFormulaCell(cell)) return false;
    }
  }
  return true;
};

interface BuildFormulaFillEditsParams {
  patternSource: Rectangle;
  fillDestination: Rectangle;
  getCellRecord: CellLookup;
}

/** Build changed existing-cell edits for a repeated formula source pattern. */
export const buildFormulaFillEdits = ({
  patternSource,
  fillDestination,
  getCellRecord,
}: BuildFormulaFillEditsParams): IAiSpreadsheetPasteEdit[] => {
  if (!isFormulaSourceRange(patternSource, getCellRecord)) return [];

  const edits: IAiSpreadsheetPasteEdit[] = [];
  for (
    let row = fillDestination.y;
    row < fillDestination.y + fillDestination.height;
    row += 1
  ) {
    for (
      let col = fillDestination.x;
      col < fillDestination.x + fillDestination.width;
      col += 1
    ) {
      if (containsCoordinate(patternSource, col, row)) continue;

      const sourceCol =
        patternSource.x +
        positiveModulo(col - patternSource.x, patternSource.width);
      const sourceRow =
        patternSource.y +
        positiveModulo(row - patternSource.y, patternSource.height);
      const sourceCell = getCellRecord(sourceCol, sourceRow);
      const destinationCell = getCellRecord(col, row);
      if (!sourceCell?.raw_input || !destinationCell) continue;
      if (!isCellEditable(destinationCell)) continue;

      // Offset the references by the distance between the cells' OWN saved
      // coordinates, not by the on-screen distance. The two agree in an unsorted,
      // unfiltered sheet, but under an active region view display row N+1 can be
      // any saved row, and a display-space delta would rewrite the formula to
      // point at the wrong records.
      const rawInput = translateFormulaReferences(
        sourceCell.raw_input,
        destinationCell.row - sourceCell.row,
        destinationCell.col - sourceCell.col,
      );
      if (rawInput === destinationCell.raw_input) continue;
      edits.push({
        cell_id: destinationCell.id,
        raw_input: rawInput,
        kind: AI_SPREADSHEET_CELL_KIND.formula,
      });
    }
  }
  return edits;
};

export type FormulaFillShortcutAction = "down" | "right" | "range";

type FormulaFillKeyEvent = Pick<
  KeyboardEvent,
  "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey"
>;

export const matchesFormulaFillShortcut = (
  event: FormulaFillKeyEvent,
): FormulaFillShortcutAction | null => {
  if (event.altKey || event.shiftKey) return null;
  if (event.metaKey === event.ctrlKey) return null;

  const key = event.key.toLowerCase();
  if (key === "d") return "down";
  if (key === "r") return "right";
  if (key === "enter") return "range";
  return null;
};

export interface FormulaFillRequest {
  patternSource: Rectangle;
  fillDestination: Rectangle;
}

export const getFormulaFillShortcutRequest = (
  action: FormulaFillShortcutAction,
  selection: GridSelection,
): FormulaFillRequest | null => {
  const current = selection.current;
  if (!current) return null;
  const destination = current.range;

  if (action === "down") {
    if (destination.height < 2) return null;
    return {
      patternSource: { ...destination, height: 1 },
      fillDestination: destination,
    };
  }
  if (action === "right") {
    if (destination.width < 2) return null;
    return {
      patternSource: { ...destination, width: 1 },
      fillDestination: destination,
    };
  }
  if (destination.width * destination.height < 2) return null;
  return {
    patternSource: {
      x: current.cell[0],
      y: current.cell[1],
      width: 1,
      height: 1,
    },
    fillDestination: destination,
  };
};

interface FormulaFillShortcutContext {
  viewerHasFocus: boolean;
  textEditorHasFocus: boolean;
  sourceIsFormula: boolean;
}

export const shouldHandleFormulaFillShortcut = ({
  viewerHasFocus,
  textEditorHasFocus,
  sourceIsFormula,
}: FormulaFillShortcutContext): boolean =>
  viewerHasFocus && !textEditorHasFocus && sourceIsFormula;
